import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_RATIO = 40;
const MIN_RATIO = 20;
const MAX_RATIO = 80;

/**
 * 左右分割レイアウトの情報パネル幅をリサイズするためのフック。
 * driveRoutes と waypoint で共通利用。表示範囲変更機能のため削除しないこと。
 */
export const useLandscapeResize = () => {
  const [infoPanelRatio, setInfoPanelRatio] = useState<number>(DEFAULT_RATIO);
  const [isLandscapeResizing, setIsLandscapeResizing] = useState<boolean>(false);

  useEffect(() => {
    if (!isLandscapeResizing) {
      return;
    }

    const updateRatio = (clientX: number) => {
      const viewportWidth = window.innerWidth || 1;
      const nextRatio = (clientX / viewportWidth) * 100;
      const clampedRatio = Math.max(MIN_RATIO, Math.min(MAX_RATIO, nextRatio));
      setInfoPanelRatio(clampedRatio);
    };

    const handleMouseMove = (event: MouseEvent) => {
      updateRatio(event.clientX);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        updateRatio(event.touches[0].clientX);
      }
    };

    const stopResize = () => {
      setIsLandscapeResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResize);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', stopResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResize);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopResize);
    };
  }, [isLandscapeResizing]);

  const landscapeSplitStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--info-panel-ratio': `${infoPanelRatio}dvw`,
      }) as CSSProperties,
    [infoPanelRatio],
  );

  return { infoPanelRatio, setInfoPanelRatio, isLandscapeResizing, setIsLandscapeResizing, landscapeSplitStyle };
};
